/**
 * Aviation map source and layer definitions for MapLibre GL JS.
 *
 * Mirrors the React Native aviation-layers.ts hierarchy but uses
 * server-side OGC tile endpoints instead of on-device GeoPackage / MBTiles.
 */

import type { LayerSpecification } from 'maplibre-gl';

export const FIXES_SOURCE_ID = 'fixes-geojson';
export const FIR_BOUNDARIES_SOURCE_ID = 'fir-boundaries-geojson';

// ─── Raster chart layers ─────────────────────────────────────────────────────

export function createRasterChartLayer(
  id: string,
  sourceId: string,
  opacity = 0.85,
  visible = false,
): LayerSpecification {
  return {
    id,
    type: 'raster',
    source: sourceId,
    layout: { visibility: visible ? 'visible' : 'none' },
    paint: { 'raster-opacity': opacity },
  } as LayerSpecification;
}

// ─── Airspace fill + outline layers ──────────────────────────────────────────

export interface AirspaceStyleDef {
  id: string;
  sourceLayer: string;
  fillColor: string;
  fillOpacity: number;
  lineColor: string;
  lineWidth: number;
  lineDash?: number[];
  filter?: unknown[];
  minzoom?: number;
  maxzoom?: number;
}

const AIRSPACE_STYLES: AirspaceStyleDef[] = [
  { id: 'airspace-class-b',   sourceLayer: 'class_class_b', fillColor: '#3B82F6', fillOpacity: 0.10, lineColor: '#3B82F6', lineWidth: 2 },
  { id: 'airspace-class-c',   sourceLayer: 'class_class_c', fillColor: '#8B5CF6', fillOpacity: 0.08, lineColor: '#8B5CF6', lineWidth: 1.5 },
  { id: 'airspace-class-d',   sourceLayer: 'class_class_d', fillColor: '#06B6D4', fillOpacity: 0.06, lineColor: '#06B6D4', lineWidth: 1.5, lineDash: [4, 4] },
  { id: 'airspace-class-e',   sourceLayer: 'class_class_e', fillColor: '#A78BFA', fillOpacity: 0.04, lineColor: '#A78BFA', lineWidth: 1, lineDash: [6, 4] },
  { id: 'airspace-moa',       sourceLayer: 'sua_moa', fillColor: '#F97316', fillOpacity: 0.08, lineColor: '#F97316', lineWidth: 1.5, lineDash: [8, 4] },
  { id: 'airspace-restricted', sourceLayer: 'sua_r', fillColor: '#EF4444', fillOpacity: 0.10, lineColor: '#EF4444', lineWidth: 2 },
  { id: 'airspace-prohibited', sourceLayer: 'sua_p', fillColor: '#DC2626', fillOpacity: 0.15, lineColor: '#DC2626', lineWidth: 2.5 },
  { id: 'airspace-warning',   sourceLayer: 'sua_w', fillColor: '#EAB308', fillOpacity: 0.08, lineColor: '#EAB308', lineWidth: 1.5 },
  { id: 'airspace-alert',     sourceLayer: 'sua_a', fillColor: '#F97316', fillOpacity: 0.06, lineColor: '#F97316', lineWidth: 1 },
  { id: 'airspace-tfr',       sourceLayer: 'boundary_airspace_nda_tfr', fillColor: '#EF4444', fillOpacity: 0.20, lineColor: '#EF4444', lineWidth: 2.5, lineDash: [2, 2] },
];

export function createAirspaceFillLayer(def: AirspaceStyleDef): LayerSpecification {
  return {
    id: `${def.id}-fill`,
    type: 'fill',
    source: 'airspaces-vt',
    'source-layer': def.sourceLayer,
    ...(def.filter ? { filter: def.filter } : {}),
    minzoom: def.minzoom,
    maxzoom: def.maxzoom,
    layout: { visibility: 'visible' },
    paint: { 'fill-color': def.fillColor, 'fill-opacity': def.fillOpacity },
  } as LayerSpecification;
}

export function createAirspaceLineLayer(def: AirspaceStyleDef): LayerSpecification {
  const paint: Record<string, unknown> = {
    'line-color': def.lineColor,
    'line-width': def.lineWidth,
  };
  if (def.lineDash) paint['line-dasharray'] = def.lineDash;
  return {
    id: `${def.id}-line`,
    type: 'line',
    source: 'airspaces-vt',
    'source-layer': def.sourceLayer,
    ...(def.filter ? { filter: def.filter } : {}),
    minzoom: def.minzoom,
    maxzoom: def.maxzoom,
    layout: { visibility: 'visible' },
    paint,
  } as LayerSpecification;
}

export function getAllAirspaceStyles(): AirspaceStyleDef[] {
  return AIRSPACE_STYLES;
}

// ─── Airport symbol layer ────────────────────────────────────────────────────

export function createAirportSymbolLayer(sourceId = 'airports-vt', sourceLayer = 'airports'): LayerSpecification {
  return {
    id: 'airports-symbol',
    type: 'circle',
    source: sourceId,
    'source-layer': sourceLayer,
    layout: { visibility: 'visible' },
    paint: {
      'circle-radius': ['interpolate', ['linear'], ['zoom'], 5, 2, 10, 5, 14, 8],
      'circle-color': ['match', ['get', 'type'],
        'AIRPORT',  '#3B82F6',
        'HELIPORT', '#22C55E',
        'SEAPLANE BASE', '#06B6D4',
        '#CBD5E1',
      ],
      'circle-stroke-width': 1,
      'circle-stroke-color': '#0F172A',
    },
  } as LayerSpecification;
}

// ─── Obstacle symbol layer ───────────────────────────────────────────────────

export function createObstacleLayer(sourceId = 'obstacles-vt', sourceLayer = 'obstacles'): LayerSpecification {
  return {
    id: 'obstacles-symbol',
    type: 'circle',
    source: sourceId,
    'source-layer': sourceLayer,
    minzoom: 8,
    layout: { visibility: 'none' },
    paint: {
      'circle-radius': ['interpolate', ['linear'], ['zoom'], 8, 1.5, 12, 4, 16, 7],
      'circle-color': ['case',
        ['>=', ['get', 'agl_ft'], 1000], '#EF4444',
        ['>=', ['get', 'agl_ft'], 500],  '#F97316',
        '#EAB308',
      ],
      'circle-stroke-width': 1,
      'circle-stroke-color': '#0F172A',
    },
  } as LayerSpecification;
}

// ─── Navaid symbol layer ─────────────────────────────────────────────────────

export function createNavaidLayer(sourceId = 'navaids-vt', sourceLayer = 'navaids'): LayerSpecification {
  return {
    id: 'navaids-symbol',
    type: 'circle',
    source: sourceId,
    'source-layer': sourceLayer,
    minzoom: 6,
    layout: { visibility: 'none' },
    paint: {
      'circle-radius': ['interpolate', ['linear'], ['zoom'], 6, 2, 12, 6],
      'circle-color': '#22D3EE',
      'circle-stroke-width': 1,
      'circle-stroke-color': '#0F172A',
    },
  } as LayerSpecification;
}

// ─── Airways, fixes, and FIR/UIR navigation overlays ────────────────────────

export function createAirwayLayer(sourceId = 'airways-vt', sourceLayer = 'airways'): LayerSpecification {
  return {
    id: 'airways-line',
    type: 'line',
    source: sourceId,
    'source-layer': sourceLayer,
    minzoom: 5,
    layout: { visibility: 'none' },
    paint: {
      'line-color': '#A78BFA',
      'line-width': ['interpolate', ['linear'], ['zoom'], 5, 0.8, 10, 1.8],
      'line-opacity': 0.75,
    },
  } as LayerSpecification;
}

export function createFixLayer(sourceId = FIXES_SOURCE_ID): LayerSpecification {
  return {
    id: 'fixes-circle',
    type: 'circle',
    source: sourceId,
    minzoom: 6,
    layout: { visibility: 'none' },
    paint: {
      'circle-radius': ['interpolate', ['linear'], ['zoom'], 6, 2, 12, 4],
      'circle-color': '#FACC15',
      'circle-stroke-width': 1,
      'circle-stroke-color': '#422006',
    },
  } as LayerSpecification;
}

export function createFixLabelLayer(sourceId = FIXES_SOURCE_ID): LayerSpecification {
  return {
    id: 'fixes-label',
    type: 'symbol',
    source: sourceId,
    minzoom: 8,
    layout: {
      visibility: 'none',
      'text-field': ['coalesce', ['get', 'ident'], ['get', 'IDENT'], ['get', 'name'], ''],
      'text-size': 10,
      'text-offset': [0, 1],
      'text-anchor': 'top',
    },
    paint: {
      'text-color': '#FEF08A',
      'text-halo-color': '#0F172A',
      'text-halo-width': 1,
    },
  } as LayerSpecification;
}

export function createFirBoundaryLayer(sourceId = FIR_BOUNDARIES_SOURCE_ID): LayerSpecification {
  return {
    id: 'fir-boundaries-line',
    type: 'line',
    source: sourceId,
    layout: { visibility: 'none' },
    paint: {
      'line-color': '#FB923C',
      'line-width': 1.5,
      'line-dasharray': [4, 3],
      'line-opacity': 0.8,
    },
  } as LayerSpecification;
}
